import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useFunnelEditor from '../hooks/useFunnelEditor';
import EditorToolbar from '../components/editor/EditorToolbar';
import BrandingSidebar from '../components/editor/BrandingSidebar';
import EditableHeroSection from '../components/editor/EditableHeroSection';
import EditableTrustBlock from '../components/editor/EditableTrustBlock';

/**
 * FunnelEditor - Main funnel editing page
 *
 * URL: /editor/:funnelId/:pageId
 *
 * Features:
 * - Inline text editing
 * - Branding sidebar
 * - Auto-save with debounce
 * - Undo/redo support
 * - Live preview toggle
 * - Publish workflow
 */
export default function FunnelEditor() {
  const { funnelId, pageId } = useParams();
  const [isEditing, setIsEditing] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const {
    funnel,
    page,
    branding,
    loading,
    saving,
    error,
    hasChanges,
    lastSaved,
    canUndo,
    canRedo,
    updateSection,
    updateBranding,
    save,
    publish,
    undo,
    redo
  } = useFunnelEditor(funnelId, pageId);

  // Parse sections from page content
  const sections = useMemo(() => {
    return page?.content?.sections || [];
  }, [page]);

  // Handle section updates
  const handleSectionUpdate = (sectionIndex, updates) => {
    updateSection(sectionIndex, updates);
  };

  // Open preview in new tab
  const handlePreview = () => {
    // Construct preview URL based on funnel slug
    const previewUrl = funnel?.slug
      ? `/${funnel.slug}/${page?.slug || ''}`
      : `/preview/${funnelId}/${pageId}`;

    window.open(previewUrl, '_blank');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-600">Loading funnel editor...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top toolbar */}
      <EditorToolbar
        isEditing={isEditing}
        onToggleEdit={() => setIsEditing(!isEditing)}
        hasChanges={hasChanges}
        saving={saving}
        lastSaved={lastSaved}
        canUndo={canUndo}
        canRedo={canRedo}
        onSave={save}
        onPublish={publish}
        onUndo={undo}
        onRedo={redo}
        onPreview={handlePreview}
        funnelName={funnel?.name || 'Untitled Funnel'}
        pageName={page?.title || 'Page'}
      />

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Branding sidebar */}
        <BrandingSidebar
          branding={branding}
          onChange={updateBranding}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />

        {/* Page preview/editor */}
        <div className="flex-1 overflow-y-auto">
          {/* Edit mode indicator */}
          {isEditing && (
            <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-center">
              <span className="text-sm text-blue-700">
                ✎ Click on any text to edit it directly
              </span>
            </div>
          )}

          {/* Funnel page content with branding applied */}
          <div
            style={{
              '--brand-primary': branding.primary_color || '#1e40af',
              '--brand-accent': branding.secondary_color || '#AEFFE3',
              '--brand-text': branding.text_color || '#1f2937',
              '--brand-bg': branding.background_color || '#ffffff',
              fontFamily: branding.fonts?.body || 'Inter'
            }}
          >
            {sections.map((section, index) => {
              switch (section.type) {
                case 'hero':
                  return (
                    <EditableHeroSection
                      key={index}
                      {...section.content}
                      branding={branding}
                      isEditing={isEditing}
                      onUpdate={(updates) => handleSectionUpdate(index, updates)}
                    />
                  );

                case 'trust_block':
                case 'trust':
                  return (
                    <EditableTrustBlock
                      key={index}
                      {...section.content}
                      branding={branding}
                      isEditing={isEditing}
                      onUpdate={(updates) => handleSectionUpdate(index, updates)}
                    />
                  );

                // Add more section types as needed
                // case 'testimonials':
                // case 'features':
                // case 'faq':
                // case 'cta':

                default:
                  return (
                    <div key={index} className="p-8 bg-gray-50 border border-dashed border-gray-300 text-center text-gray-500">
                      Unknown section type: {section.type}
                    </div>
                  );
              }
            })}

            {/* Empty state */}
            {sections.length === 0 && (
              <div className="p-16 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No sections yet</h3>
                <p className="text-gray-500 mb-6">
                  This page doesn't have any content sections.
                </p>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                  Add Section
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Keyboard shortcuts help */}
      <div className="fixed bottom-4 right-4">
        <details className="bg-white rounded-lg shadow-lg border border-gray-200 text-sm">
          <summary className="px-3 py-2 cursor-pointer text-gray-600 hover:text-gray-900">
            ⌨️ Shortcuts
          </summary>
          <div className="px-3 py-2 border-t border-gray-100 space-y-1 text-gray-600">
            <div><kbd className="px-1 bg-gray-100 rounded text-xs">Ctrl+S</kbd> Save</div>
            <div><kbd className="px-1 bg-gray-100 rounded text-xs">Ctrl+Z</kbd> Undo</div>
            <div><kbd className="px-1 bg-gray-100 rounded text-xs">Ctrl+Shift+Z</kbd> Redo</div>
            <div><kbd className="px-1 bg-gray-100 rounded text-xs">Esc</kbd> Cancel edit</div>
          </div>
        </details>
      </div>
    </div>
  );
}
